/**
 * Where the two chat-view controls live: "Whole conversation" (the transcript
 * toggle) and "Edit Content" (the working-draft drawer).
 *
 * #1928, from Ben's iPhone review: on a phone the nav row read
 * [Chat ▾ | Edit Content] and the chat heading read [title | Whole
 * conversation]. The tab row is navigation, so the control that changes *what
 * you are reading in the chat* belongs there next to Chat, and the control that
 * opens the draft belongs on the chat itself.
 *
 * The decision lives here, in one function over the three inputs that matter,
 * because it spans three components — the item page owns the nav row, the chat
 * pane owns the heading, and the panel sits between them. Spread across those
 * files as three ad-hoc `v-if`s it could not be read in one place or tested at
 * all; this repo's vitest has no vue plugin, so SFCs cannot be mounted.
 */
import { POST_CONTENT_TAB } from './view-post-content';

export type ChatControl = 'edit-content' | 'whole-conversation' | 'none';

/** The unified-layout breakpoint. Matches `showWorkingDraftToggle` on the item page. */
export const COMPACT_VIEWPORT_MAX_WIDTH = 1024;

export interface ChatControlPlacementInput {
  /** Viewport ≤1024px — the breakpoint at which the draft becomes a drawer. */
  isCompactViewport: boolean;
  /** The active unified tab on the item page. */
  activeTab: string;
  /** Whether there is anything to show in the transcript (`canShowTranscript`). */
  hasTranscript: boolean;
  /** MIB V2 chrome: Thread replaces the Whole conversation toggle. */
  makeItBetterV2?: boolean;
}

export interface ChatControlPlacement {
  /** Rendered in the IdeaTabsBar `#actions` slot, opposite the tab control. */
  navRow: ChatControl;
  /** Rendered in the chat pane heading, opposite the chat title. */
  chatHeader: ChatControl;
}

export const CHAT_TAB = 'chat';

export function isCompactViewportWidth(width: number): boolean {
  return width <= COMPACT_VIEWPORT_MAX_WIDTH;
}

/**
 * Desktop is deliberately untouched (#1928 AC 5): the working draft is a
 * permanent sidebar there, so there is no Edit Content button to place, and the
 * transcript toggle stays in the chat heading where it has always been.
 *
 * On mobile the swap only applies to the Chat tab. The nav-row Edit Content
 * button is currently the ONLY working-draft entry point on Post Content, Files,
 * Settings, History and Comments — moving it into the chat heading
 * unconditionally would strand the drawer on every one of those tabs, which the
 * issue's own "Edit Content stays available" forbids.
 */
export function chatControlPlacement(input: ChatControlPlacementInput): ChatControlPlacement {
  if (input.makeItBetterV2) {
    // Edit Content lives on the Post Content view, not the nav row or chat heading.
    return { navRow: 'none', chatHeader: 'none' };
  }

  if (!input.isCompactViewport) {
    return {
      navRow: 'none',
      chatHeader: input.hasTranscript ? 'whole-conversation' : 'none'
    };
  }

  if (input.activeTab === POST_CONTENT_TAB) {
    return { navRow: 'none', chatHeader: 'none' };
  }

  if (input.activeTab !== CHAT_TAB) {
    return { navRow: 'edit-content', chatHeader: 'none' };
  }

  return {
    // No inert affordance: a session with nothing recorded yet gets no button
    // at all rather than a dead one (#1928 AC 3).
    navRow: input.hasTranscript ? 'whole-conversation' : 'none',
    chatHeader: 'edit-content'
  };
}
